import { useEffect, useMemo, useState } from "react";
import { Megaphone, X, Clock } from "lucide-react";

/**
 * Phase-based announcements.
 * - Pre  → before tryouts start (Aug 11, 2026 · 00:00 CT). Shows countdown.
 * - Live → during tryouts (Aug 11 00:00 CT → Aug 13 00:00 CT). Swaps countdown to "TRYOUTS LIVE".
 * - Post → after tryouts end (Aug 13, 2026 · 00:00 CT). Auto-swaps to fallback messages.
 *
 * Each phase has its own dismissal key so a visitor who closed the pre-tryout bar
 * still sees the "Live" and "Post" announcements when they arrive.
 */
const TRYOUTS_START = new Date("2026-08-11T00:00:00-05:00").getTime();
const TRYOUTS_END = new Date("2026-08-13T00:00:00-05:00").getTime();

const PHASES = {
  pre: {
    id: "tryouts-pre-v1",
    items: [
      "Team BBA Elite Try-Outs · Aug 11 & 12",
      "Come learn about Ambassador NIL Scholarship Opportunities",
    ],
    showCountdown: true,
    liveLabel: null,
  },
  live: {
    id: "tryouts-live-v1",
    items: [
      "Team BBA Elite Try-Outs happening now · Aug 11 & 12",
      "Ambassador NIL Scholarship info available on site",
    ],
    showCountdown: false,
    liveLabel: "Tryouts Live",
  },
  post: {
    id: "post-tryouts-v1",
    items: [
      "Team BBA Elite Roster · Announcement Coming Soon",
      "Ambassador NIL Scholarship applications now open",
      "Fall Skill Development & Shooting Camps enrolling",
    ],
    showCountdown: false,
    liveLabel: null,
  },
};

function getPhase(now) {
  if (now < TRYOUTS_START) return PHASES.pre;
  if (now < TRYOUTS_END) return PHASES.live;
  return PHASES.post;
}

// Backwards-compat exports used by Landing to hydrate dismissed state
export const ANNOUNCEMENT_VERSION = "v2";
export function getDismissKey(now = Date.now()) {
  return `bba_announcement_dismissed_${getPhase(now).id}`;
}
// Default export used at module load — Landing re-computes at render
export const DISMISS_KEY = getDismissKey();

function formatDelta(ms) {
  if (ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  return { days, hours, minutes, seconds };
}

export default function AnnouncementBar({ onDismiss }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const delta = useMemo(() => formatDelta(TRYOUTS_START - now), [now]);
  const phase = useMemo(() => getPhase(now), [now]);
  const dismissKey = useMemo(() => `bba_announcement_dismissed_${phase.id}`, [phase]);
  const loop = useMemo(
    () => Array.from({ length: 3 }).flatMap(() => phase.items),
    [phase],
  );

  const dismiss = () => {
    try {
      localStorage.setItem(dismissKey, "1");
    } catch {
      /* no-op */
    }
    onDismiss?.(dismissKey);
  };

  return (
    <div
      data-testid="announcement-bar"
      className="fixed top-0 inset-x-0 z-[60] h-10 bg-white text-[#050B14] border-b border-[#0A1128]/10 overflow-hidden select-none"
    >
      <div className="max-w-full h-full flex items-center">
        {/* left icon badge */}
        <div className="hidden sm:flex items-center gap-2 h-full px-4 bg-[#050B14] text-white flex-shrink-0">
          <Megaphone size={14} className="text-white" />
          <span className="text-[10px] tracking-[0.25em] uppercase font-bold">
            Latest
          </span>
        </div>

        {/* marquee track (shrinkable) */}
        <div className="marquee flex-1 min-w-0 h-full flex items-center whitespace-nowrap relative">
          <div className="marquee-track flex items-center gap-10 pl-6">
            {loop.map((text, i) => (
              <span
                key={`a-${i}`}
                className="inline-flex items-center gap-10 text-[13px] font-semibold tracking-wide"
              >
                {text}
                <Dot />
              </span>
            ))}
          </div>
          <div
            className="marquee-track flex items-center gap-10 pl-6"
            aria-hidden="true"
          >
            {loop.map((text, i) => (
              <span
                key={`b-${i}`}
                className="inline-flex items-center gap-10 text-[13px] font-semibold tracking-wide"
              >
                {text}
                <Dot />
              </span>
            ))}
          </div>
          {/* right fade so countdown/close read clean */}
          <div className="pointer-events-none absolute right-0 top-0 h-full w-16 bg-gradient-to-l from-white to-transparent" />
        </div>

        {/* countdown / live badge — phase-aware */}
        {phase.showCountdown && delta ? (
          <div
            data-testid="announcement-countdown"
            className="hidden md:inline-flex items-center gap-2 h-full px-3 border-l border-[#0A1128]/10 flex-shrink-0 bg-white"
            aria-label="Time until tryouts"
          >
            <Clock size={12} className="text-[#050B14]" />
            <div className="flex items-baseline gap-1.5 text-[#050B14]">
              <CountUnit value={delta.days} label="d" />
              <CountUnit value={delta.hours} label="h" pad />
              <CountUnit value={delta.minutes} label="m" pad />
              <CountUnit value={delta.seconds} label="s" pad hideOnSm />
            </div>
          </div>
        ) : phase.liveLabel ? (
          <div
            data-testid="announcement-live"
            className="hidden md:inline-flex items-center h-full px-3 border-l border-[#0A1128]/10 flex-shrink-0 bg-[#050B14] text-white"
          >
            <span className="inline-block w-2 h-2 rounded-full bg-red-400 mr-2 animate-pulse" />
            <span className="text-[11px] font-bold tracking-[0.2em] uppercase">
              {phase.liveLabel}
            </span>
          </div>
        ) : null}

        {/* close */}
        <button
          type="button"
          onClick={dismiss}
          data-testid="announcement-dismiss"
          aria-label="Dismiss announcement"
          className="h-full px-3 sm:px-4 flex items-center text-[#050B14]/70 hover:text-[#050B14] border-l border-[#0A1128]/10 flex-shrink-0 hover:bg-[#050B14]/5 transition-colors"
        >
          <X size={15} />
        </button>
      </div>
    </div>
  );
}

const Dot = () => (
  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#050B14]/40" />
);

const CountUnit = ({ value, label, pad, hideOnSm }) => (
  <span
    className={`inline-flex items-baseline ${
      hideOnSm ? "hidden lg:inline-flex" : ""
    }`}
  >
    <span className="font-display text-sm tabular-nums">
      {pad ? String(value).padStart(2, "0") : value}
    </span>
    <span className="text-[9px] uppercase tracking-widest text-[#050B14]/60 ml-0.5">
      {label}
    </span>
  </span>
);
